/**
 * FavoritesService — saved addresses (Home, Work, custom labels)
 * 
 * Loads favorites from DynamoDB on sign-in, caches locally.
 * Integrates with AutocompleteService to show matching favorites
 * at the top of suggestions when user types a matching label.
 * 
 * API: GET/POST/PUT/DELETE /api/favorites (auth required)
 */

import { authFetch } from './authFetch.js';

const API_URL = import.meta.env.VITE_BACKEND_URL || 'https://6wdv4xnpei.execute-api.us-west-1.amazonaws.com/prod';

export class FavoritesService {
    constructor() {
        this.favorites = [];
        this.loaded = false;
    }

    /**
     * Load favorites from server. Call on sign-in.
     */
    async load() {
        try {
            const response = await authFetch(`${API_URL}/api/favorites`);
            if (!response.ok) {
                if (window.showToast) window.showToast(`Favorites load failed: HTTP ${response.status}`, { type: 'warning' });
                return;
            }
            const data = await response.json();
            if (data.status === 'success') {
                this.favorites = data.favorites || [];
                this.loaded = true;
            } else if (window.showToast) {
                window.showToast(`Favorites load failed: ${data.message || JSON.stringify(data)}`, { type: 'warning' });
            }
        } catch (err) {
            console.warn('Failed to load favorites:', err.message);
            if (window.showToast) window.showToast(`Favorites load exception: ${err.message}`, { type: 'warning' });
        }
    }

    /**
     * Get all favorites.
     */
    getAll() {
        return this.favorites;
    }

    /**
     * Find favorites matching a search string (label or address).
     * Used by autocomplete to show favorites at the top of suggestions.
     */
    match(query) {
        if (!query || query.length < 1) return [];
        const q = query.toLowerCase();
        return this.favorites.filter(f =>
            f.label.toLowerCase().includes(q) ||
            (f.address && f.address.toLowerCase().includes(q))
        );
    }

    /**
     * Add a new favorite.
     */
    async add(label, lat, lng, address) {
        try {
            const response = await authFetch(`${API_URL}/api/favorites`, {
                method: 'POST',
                body: JSON.stringify({ label, lat, lng, address })
            });
            const data = await response.json();
            if (data.status === 'success') {
                this.favorites.push(data.favorite);
                return data.favorite;
            }
            console.error('Favorites add failed:', data);
            alert('Save error: ' + (data.message || JSON.stringify(data)));
        } catch (err) {
            console.error('Failed to add favorite:', err.message);
            alert('Save exception: ' + err.message);
        }
        return null;
    }

    /**
     * Update an existing favorite.
     */
    async update(id, updates) {
        try {
            const response = await authFetch(`${API_URL}/api/favorites`, {
                method: 'PUT',
                body: JSON.stringify({ id, ...updates })
            });
            const data = await response.json();
            if (data.status === 'success') {
                const idx = this.favorites.findIndex(f => f.id === id);
                if (idx !== -1) this.favorites[idx] = data.favorite;
                return data.favorite;
            }
        } catch (err) {
            console.error('Failed to update favorite:', err.message);
        }
        return null;
    }

    /**
     * Delete a favorite.
     */
    async delete(id) {
        try {
            const response = await authFetch(`${API_URL}/api/favorites`, {
                method: 'DELETE',
                body: JSON.stringify({ id })
            });
            const data = await response.json();
            if (data.status === 'success') {
                this.favorites = this.favorites.filter(f => f.id !== id);
                return true;
            }
        } catch (err) {
            console.error('Failed to delete favorite:', err.message);
        }
        return false;
    }

    /**
     * Get a favorite by label (case-insensitive exact match).
     * Used for "home", "work" shortcuts.
     */
    getByLabel(label) {
        const q = label.toLowerCase();
        return this.favorites.find(f => f.label.toLowerCase() === q) || null;
    }
}

export const favoritesService = new FavoritesService();
